import mainApiClient from "../../helpers/services/main-api-client";

export interface ApiCampaign {
  id: string;
  subject: string;
  sentTo: number;
  audience: string;
  opened: string;
  openedCount: number;
  clicked: string;
  clickedCount: number;
  bounced: string;
  bouncedCount: number;
  unsubscribed?: string;
  unsubscribedCount?: number;
  complaints: string;
  complaintsCount: number;
  date: string;
  sentAt?: string | null;
  scheduledAt?: string | null;
  updatedAt?: string | null;
  status: "sent" | "draft" | "scheduled";
  accent: string;
  createType?: string;
  sendgridId?: string;
  sendgridListId?: string | null;
  htmlContent?: string;
  audienceInfo?: {
    listName: string;
    listId: string | null;
    isLocalList: boolean;
    contactCount: number;
    fromEmail: string | null;
    fromName: string | null;
  };
  sendLogs?: {
    id: number;
    sendType: string;
    recipientCount: number;
    fromEmail: string | null;
    createdAt: string;
    metadata: Record<string, unknown> | null;
  }[];
}

export interface ApiSubscriber {
  email: string;
  name: string;
  list: string;
  updatedAt: string;
  source?: string;
  contactId?: number | null;
}

export interface ApiImportableContact {
  id: number;
  email: string;
  name: string;
  source: string;
  alreadySubscribed: boolean;
}

export interface ApiTemplate {
  id: string;
  name: string;
  type: string;
  updatedAt: string;
  accent: string;
}

export interface ApiTemplateDetail extends ApiTemplate {
  subject: string;
  html: string;
  plainContent?: string;
  editor?: string;
}

export interface ApiDeliveryHealth {
  mode: "marketing" | "local" | "transactional_only";
  readyForBulk: boolean;
  maxRecipientsWithoutMarketing: number;
  sendgridListId: string | null;
  checks: {
    marketingApi: boolean;
    marketingList: boolean;
    localFallback: boolean;
    listContactCount: number;
    senderConfigured: boolean;
    senderVerified: boolean;
    domainConfigured: boolean;
    domainAuthenticated: boolean;
  };
  issues: { code: string; message: string; detail?: string }[];
}

export interface ApiConfig {
  engine: string;
  senders: { email: string; name: string; verified: boolean }[];
  activeSender?: { email: string; name: string; verified: boolean } | null;
  unsubscribeGroups: {
    id: number;
    name: string;
    description: string;
    count: number;
  }[];
  domains: { domain: string; authenticated: boolean }[];
  delivery?: ApiDeliveryHealth;
}

export interface ApiOverview {
  lists: number;
  subscribers: number;
  fields: number;
  tags: number;
  sentCount: number;
  draftCount: number;
  scheduledCount: number;
  averageOpenRate: string;
}

export interface ApiMarketingList {
  id: string;
  name: string;
  description: string;
  subscribers: number;
  isDefault: boolean;
  isLocal: boolean;
}

export interface ApiListSetting {
  uniqueId: string;
  listName: string;
  description: string;
  sendgridListId: string;
  fromEmail?: string;
  fromName?: string;
  physicalAddress?: {
    label: string;
    line1?: string;
    city?: string;
    region?: string;
    country?: string;
    postalCode?: string;
    isCurrent?: boolean;
  } | null;
  notificationContacts?: { email: string; name: string }[];
}

export interface ApiCustomField {
  id: string;
  name: string;
  type: "text" | "number" | "date";
  subscriberCanUpdate: boolean;
}

export interface ApiListSegment {
  id: string;
  name: string;
  contacts: number;
  status: "active" | "draft";
}

export interface ApiListTag {
  id: string;
  name: string;
  nameId: string;
  subscribers: number;
  crmContacts?: number;
  listSubscribers?: number;
  textColor?: string;
  backgroundColor?: string;
  archived: boolean;
}

export interface ApiPhysicalAddressOptions {
  current: ApiListSetting["physicalAddress"];
  options: NonNullable<ApiListSetting["physicalAddress"]>[];
}

export interface ApiBillingDashboardCompany {
  companyId: number;
  companyName: string;
  listId: string | null;
  isLocalList: boolean;
  listName: string | null;
  fromEmail: string | null;
  domain: string | null;
  domainAuthenticated: boolean;
  asmGroupId: number | null;
  totalRecipients: number;
  sendOperations: number;
  listContactCount: number;
  crmContactsWithEmail: number;
  marketingReady: boolean;
  sendTypeBreakdown: Record<string, { recipients: number; operations: number }>;
  lastSendAt: string | null;
  billingRisk: {
    contactPlanPercent: number;
    periodEmailPlanPercent: number;
    crmNotSyncedCount: number;
  };
}

export interface ApiBillingDashboard {
  period: { from: string; to: string };
  sendGridAccount: {
    credits: { remain: number | null; total: number | null; used: number | null } | null;
    marketingPlan: string;
    marketingPlanLimits: {
      contacts: number;
      marketingEmailsPerMonth: number;
      emailApiPerMonth: number;
    };
  };
  totals: {
    totalRecipients: number;
    totalSendOperations: number;
    companiesWithActivity: number;
    companiesConfigured: number;
    totalListContacts: number;
    totalCrmContactsWithEmail: number;
    remainingMarketingEmails: number;
    remainingMarketingContacts: number;
    projectedFullAudienceRecipients: number;
    projectedFullAudienceWouldExceed: boolean;
    companiesLocalList: number;
    companiesMissingSender: number;
    companiesMissingDomainAuth: number;
    marketingEmailsUsedPercent: number;
    marketingContactsUsedPercent: number;
    emailApiUsedPercent: number | null;
  };
  companies: ApiBillingDashboardCompany[];
  daily: { day: string; totalRecipients: number; sendOperations: number }[];
  bySendType: { sendType: string; totalRecipients: number; sendOperations: number }[];
  recentLogs: {
    id: number;
    companyId: number;
    companyName: string | null;
    campaignId: string | null;
    sendType: string;
    recipientCount: number;
    fromEmail: string | null;
    subject: string | null;
    createdAt: string;
    metadata: Record<string, unknown> | null;
  }[];
}

export interface ApiSendGridInvoice {
  id: string;
  invoiceNumber: string;
  status: string;
  amount: number | string | null;
  unpaidBalance?: number | string | null;
  currency: string;
  periodStart: string | null;
  periodEnd: string | null;
  invoiceDate: string | null;
  paidAt: string | null;
  pdfUrl: string | null;
  pdfStatus?: string | null;
  description: string | null;
}

export interface ApiSendGridInvoicesPayload {
  invoices: ApiSendGridInvoice[];
  syncedAt: string | null;
  fingerprint: string | null;
  fromCache: boolean;
  changed: boolean;
  lastSyncError: string | null;
  billingApiAvailable: boolean | null;
  needsBillingKey: boolean;
  totalPaid: number;
}

export interface ApiDomainDns {
  domain: string | null;
  authenticated?: boolean;
  records: { type: string; host: string; value: string; valid?: boolean }[];
}

const unwrap = <T>(promise: Promise<{ data: { payload: T } }>) =>
  promise.then(({ data }) => data.payload);

export const emailMarketingApiService = {
  getOverview(companyId: string) {
    return unwrap<{ overview: ApiOverview }>(
      mainApiClient().get("email-marketing/overview", { params: { companyId } })
    ).then((p) => p.overview);
  },
  getConfig(companyId: string) {
    return unwrap<{ config: ApiConfig }>(
      mainApiClient().get("email-marketing/config", { params: { companyId } })
    ).then((p) => p.config);
  },
  getList(companyId: string) {
    return unwrap<{ list: ApiListSetting }>(
      mainApiClient().get("email-marketing/list", { params: { companyId } })
    ).then((p) => p.list);
  },
  getMarketingLists(companyId: string) {
    return unwrap<{ defaultListId: string | null; lists: ApiMarketingList[] }>(
      mainApiClient().get("email-marketing/lists", { params: { companyId } })
    );
  },
  createMarketingList(
    companyId: string,
    body: { name: string; description?: string }
  ) {
    return unwrap<{ list: ApiMarketingList }>(
      mainApiClient().post("email-marketing/lists", body, {
        params: { companyId },
      })
    ).then((p) => p.list);
  },
  updateList(companyId: string, body: {
    listId?: string;
    listName?: string;
    description?: string;
    fromEmail?: string;
    fromName?: string;
    physicalAddress?: ApiListSetting["physicalAddress"];
  }) {
    return unwrap<{ list: ApiListSetting }>(
      mainApiClient().put("email-marketing/list", body, { params: { companyId } })
    ).then((p) => p.list);
  },
  getSubscribers(companyId: string, listId?: string) {
    return unwrap<{ subscribers: ApiSubscriber[] }>(
      mainApiClient().get("email-marketing/subscribers", {
        params: { companyId, listId: listId || undefined },
      })
    ).then((p) => p.subscribers);
  },
  importSubscribers(
    companyId: string,
    body: { contactIds?: number[]; sources?: string[] }
  ) {
    return unwrap<{ imported: number; created?: number; skipped?: number }>(
      mainApiClient().post("email-marketing/subscribers/import", body, {
        params: { companyId },
      })
    );
  },
  getImportableContacts(
    companyId: string,
    params?: { sources?: string[]; search?: string }
  ) {
    return unwrap<{ contacts: ApiImportableContact[] }>(
      mainApiClient().get("email-marketing/subscribers/importable", {
        params: {
          companyId,
          sources: params?.sources?.join(",") || undefined,
          search: params?.search || undefined,
        },
      })
    ).then((p) => p.contacts);
  },
  deleteSubscriber(companyId: string, email: string, listId?: string) {
    return unwrap<{ email: string }>(
      mainApiClient().delete("email-marketing/subscribers", {
        params: { companyId, email, listId: listId || undefined },
      })
    );
  },
  importSubscribersBulk(
    companyId: string,
    body: { paste?: string; contacts?: { email: string; name?: string }[] }
  ) {
    return unwrap<{ imported: number; created: number; skipped: number }>(
      mainApiClient().post("email-marketing/subscribers/bulk", body, {
        params: { companyId },
      })
    );
  },
  createSubscriber(companyId: string, body: { email: string; name?: string }) {
    return unwrap<{ subscriber: ApiSubscriber & { created?: boolean } }>(
      mainApiClient().post("email-marketing/subscribers", body, {
        params: { companyId },
      })
    ).then((p) => p.subscriber);
  },
  getSuppressions(companyId: string) {
    return unwrap<{ suppressions: { email: string; reason: string; date: string }[] }>(
      mainApiClient().get("email-marketing/suppressions", { params: { companyId } })
    ).then((p) => p.suppressions);
  },
  getCampaigns(companyId: string) {
    return unwrap<{ campaigns: ApiCampaign[] }>(
      mainApiClient().get("email-marketing/campaigns", { params: { companyId } })
    ).then((p) => p.campaigns);
  },
  getCampaign(companyId: string, campaignId: string) {
    return unwrap<{ campaign: ApiCampaign }>(
      mainApiClient().get(`email-marketing/campaigns/${campaignId}`, {
        params: { companyId },
      })
    ).then((p) => p.campaign);
  },
  createCampaign(
    companyId: string,
    body: {
      subject?: string;
      audience?: string;
      createType?: string;
      html?: string;
      sendgridListId?: string;
      listId?: string;
    }
  ) {
    return unwrap<{ campaign: ApiCampaign }>(
      mainApiClient().post("email-marketing/campaigns", body, {
        params: { companyId },
      })
    ).then((p) => p.campaign);
  },
  updateCampaign(
    companyId: string,
    campaignId: string,
    body: {
      subject?: string;
      audience?: string;
      html?: string;
      sendgridListId?: string;
      listId?: string;
    }
  ) {
    return unwrap<{ campaign: ApiCampaign }>(
      mainApiClient().put(`email-marketing/campaigns/${campaignId}`, body, {
        params: { companyId },
      })
    ).then((p) => p.campaign);
  },
  deleteCampaign(companyId: string, campaignId: string) {
    return unwrap<{ id: string }>(
      mainApiClient().delete(`email-marketing/campaigns/${campaignId}`, {
        params: { companyId },
      })
    );
  },
  cancelScheduleCampaign(companyId: string, campaignId: string) {
    return unwrap<{ campaign: ApiCampaign }>(
      mainApiClient().put(`email-marketing/campaigns/${campaignId}/cancel-schedule`, {}, {
        params: { companyId },
      })
    ).then((p) => p.campaign);
  },
  scheduleCampaign(companyId: string, campaignId: string, body?: { sendAt?: string }) {
    return unwrap<{ campaign: ApiCampaign }>(
      mainApiClient().put(`email-marketing/campaigns/${campaignId}/schedule`, body || {}, {
        params: { companyId },
      })
    ).then((p) => p.campaign);
  },
  sendCampaignNow(
    companyId: string,
    campaignId: string,
    body?: { notifyEmail?: string; html?: string; fromEmail?: string; fromName?: string }
  ) {
    return unwrap<{ campaign: ApiCampaign }>(
      mainApiClient().post(`email-marketing/campaigns/${campaignId}/send`, body || {}, {
        params: { companyId },
      })
    ).then((p) => p.campaign);
  },
  sendTest(
    companyId: string,
    campaignId: string,
    body: { email: string; html?: string; fromEmail?: string; fromName?: string }
  ) {
    return mainApiClient()
      .post(`email-marketing/campaigns/${campaignId}/test`, body, {
        params: { companyId },
      })
      .then(({ data }) => data);
  },
  getTemplates(companyId: string) {
    return unwrap<{ templates: ApiTemplate[] }>(
      mainApiClient().get("email-marketing/templates", { params: { companyId } })
    ).then((p) => p.templates);
  },
  getTemplate(companyId: string, templateId: string) {
    return unwrap<{ template: ApiTemplateDetail }>(
      mainApiClient().get(`email-marketing/templates/${templateId}`, {
        params: { companyId },
      })
    ).then((p) => p.template);
  },
  createTemplate(
    companyId: string,
    body: { name?: string; subject?: string; html?: string; editor?: string }
  ) {
    return unwrap<{ template: ApiTemplate }>(
      mainApiClient().post("email-marketing/templates", body, {
        params: { companyId },
      })
    ).then((p) => p.template);
  },
  getReportsOverview(companyId: string) {
    return unwrap<{
      reports: {
        openSeries: number[];
        openRows: { time: string; broadcasts: number; workflows: number; unique: number; total: number }[];
        campaigns: ApiCampaign[];
      };
    }>(mainApiClient().get("email-marketing/reports/overview", { params: { companyId } }))
      .then((p) => p.reports);
  },
  createSender(
    companyId: string,
    body: { email: string; name?: string; address?: string; city?: string; country?: string }
  ) {
    return unwrap<{ sender: ApiConfig["senders"][0] }>(
      mainApiClient().post("email-marketing/config/senders", body, {
        params: { companyId },
      })
    ).then((p) => p.sender);
  },
  createUnsubscribeGroup(
    companyId: string,
    body: { name: string; description?: string }
  ) {
    return unwrap<{ group: ApiConfig["unsubscribeGroups"][0] }>(
      mainApiClient().post("email-marketing/config/asm-groups", body, {
        params: { companyId },
      })
    ).then((p) => p.group);
  },
  getDomainDns(companyId: string) {
    return unwrap<{ dns: ApiDomainDns }>(
      mainApiClient().get("email-marketing/config/domain-dns", {
        params: { companyId },
      })
    ).then((p) => p.dns);
  },
  setupDomain(companyId: string, domain: string) {
    return unwrap<{ dns: ApiDomainDns }>(
      mainApiClient().post(
        "email-marketing/config/domain",
        { domain },
        { params: { companyId } }
      )
    ).then((p) => p.dns);
  },
  repairMarketingSetup(companyId: string) {
    return unwrap<{
      repaired: boolean;
      error?: string;
      delivery: ApiDeliveryHealth;
    }>(
      mainApiClient().post("email-marketing/config/repair-marketing", {}, {
        params: { companyId },
      })
    );
  },
  getCustomFields(companyId: string) {
    return unwrap<{ fields: ApiCustomField[] }>(
      mainApiClient().get("email-marketing/list/fields", { params: { companyId } })
    ).then((p) => p.fields);
  },
  createCustomField(
    companyId: string,
    body: { name: string; type?: string; subscriberCanUpdate?: boolean }
  ) {
    return unwrap<{ field: ApiCustomField }>(
      mainApiClient().post("email-marketing/list/fields", body, {
        params: { companyId },
      })
    ).then((p) => p.field);
  },
  getListSegments(companyId: string) {
    return unwrap<{ segments: ApiListSegment[] }>(
      mainApiClient().get("email-marketing/list/segments", { params: { companyId } })
    ).then((p) => p.segments);
  },
  createListSegment(companyId: string, body: { name: string }) {
    return unwrap<{ segment: ApiListSegment }>(
      mainApiClient().post("email-marketing/list/segments", body, {
        params: { companyId },
      })
    ).then((p) => p.segment);
  },
  getListTags(companyId: string) {
    return unwrap<{ tags: ApiListTag[] }>(
      mainApiClient().get("email-marketing/list/tags", { params: { companyId } })
    ).then((p) => p.tags);
  },
  createListTag(
    companyId: string,
    body: { name: string; textColor?: string; backgroundColor?: string }
  ) {
    return unwrap<{ tag: ApiListTag }>(
      mainApiClient().post("email-marketing/list/tags", body, {
        params: { companyId },
      })
    ).then((p) => p.tag);
  },
  addNotificationContact(
    companyId: string,
    body: { email: string; name?: string }
  ) {
    return unwrap<{ notificationContacts: { email: string; name: string }[] }>(
      mainApiClient().post("email-marketing/list/notifications", body, {
        params: { companyId },
      })
    ).then((p) => p.notificationContacts);
  },
  removeNotificationContact(companyId: string, email: string) {
    return unwrap<{ notificationContacts: { email: string; name: string }[] }>(
      mainApiClient().delete("email-marketing/list/notifications", {
        params: { companyId },
        data: { email },
      })
    ).then((p) => p.notificationContacts);
  },
  getPhysicalAddressOptions(companyId: string) {
    return unwrap<{ address: ApiPhysicalAddressOptions }>(
      mainApiClient().get("email-marketing/list/address-options", {
        params: { companyId },
      })
    ).then((p) => p.address);
  },
  getAdminBillingDashboard(params?: { fromDate?: string; toDate?: string; limit?: number }) {
    return unwrap<{ dashboard: ApiBillingDashboard }>(
      mainApiClient().get("email-marketing/admin/billing-dashboard", { params })
    ).then((p) => p.dashboard);
  },
  getAdminBillingInvoices() {
    return unwrap<{ invoices: ApiSendGridInvoicesPayload }>(
      mainApiClient().get("email-marketing/admin/billing-invoices")
    ).then((p) => p.invoices);
  },
  syncAdminBillingInvoices() {
    return unwrap<{ invoices: ApiSendGridInvoicesPayload }>(
      mainApiClient().post("email-marketing/admin/billing-invoices/sync")
    ).then((p) => p.invoices);
  },
};
